import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import Rating from "@mui/material/Rating";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useSurveyUiToast } from "../hooks/useSurveyUiToast";
import {
  RATING_EMOJI_SETS,
  getLikertScalePoints,
  getLikertUniqueColumns,
  getLowRatingAllowOther,
  getLowRatingEnabled,
  getLowRatingOptions,
  getLowRatingThreshold,
  getMaxLength,
  getMinLength,
  getMultiChoiceDisplay,
  getSingleChoiceDisplay,
  getNumber,
  getPlaceholder,
  getRatingFollowUp,
  getRatingScale,
  getRatingStyle,
  getTextVariant,
  getTextareaRows,
  missingRequiredKeys
} from "./questionConfig";
import { computeVisiblePath, computeVisibleSections } from "../utils/branchingPath";
function ratingFollowupAnswerKey(questionKey) {
  return `${questionKey}__rating_follow_up`;
}
function lowRatingReasonsKey(questionKey) {
  return `${questionKey}__low_rating_reasons`;
}
function lowRatingOtherKey(questionKey) {
  return `${questionKey}__low_rating_other`;
}
const RATING_FOLLOWUP_MAX_LEN = 2e3;
const LOW_RATING_OTHER_MAX_LEN = 500;
function lowRatingSelectedKeys(answers, key) {
  const v = answers[key];
  return Array.isArray(v) ? v : [];
}
function multiSelectedKeys(answers, key) {
  const v = answers[key];
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object" && Array.isArray(v.values)) return v.values;
  return [];
}
function choiceTextAnswerKey(questionKey, optionKey) {
  return `${questionKey}__${optionKey}__text`;
}
function singleSelectedKey(answers, key) {
  const v = answers[key];
  if (typeof v === "string") return v;
  if (v && typeof v === "object" && !Array.isArray(v)) return typeof v.value === "string" ? v.value : "";
  return "";
}
function choiceTextValues(answers, key) {
  const v = answers[key];
  if (v && typeof v === "object" && !Array.isArray(v) && v.textValues && typeof v.textValues === "object") {
    return v.textValues;
  }
  return {};
}
// "Other" option on single/multi questions: picking it opens an optional text
// box. The server flags it as is_other; older payloads carried the flag as
// allow_text or as a "[allow_text]" prefix on the description.
const OTHER_VALUE_PREFIX = "other:";
const OTHER_TEXT_PLACEHOLDER = "Please specify";
function isOtherOption(o) {
  return !!(o && (o.is_other || o.allow_text || String(o.description || "").startsWith("[allow_text]")));
}
function optionDescription(o) {
  return String(o?.description || "").replace(/^\[allow_text\]\s*/, "");
}
// Wire form of a chosen option: an "Other" pick with typed text is sent (and
// stored, and shown in analytics) as "other:<text>"; anything else is the key.
function submittedOptionKey(q, optionKey, textValues) {
  const o = (q.options || []).find((x) => x.option_key === optionKey);
  const typed = typeof textValues?.[optionKey] === "string" ? textValues[optionKey].trim() : "";
  return isOtherOption(o) && typed ? `${OTHER_VALUE_PREFIX}${typed}` : optionKey;
}
function ratingDisplay(style, indexZero, scale) {
  if (style === "stars") {
    const filled = "\u2605".repeat(indexZero + 1);
    const empty = "\u2606".repeat(scale - indexZero - 1);
    return filled + empty;
  }
  if (style === "hearts") {
    return "\u2665".repeat(indexZero + 1) + "\u2661".repeat(scale - indexZero - 1);
  }
  if (style === "emojis") {
    const set = RATING_EMOJI_SETS[scale] ?? RATING_EMOJI_SETS[5];
    return set[Math.min(indexZero, set.length - 1)] ?? String(indexZero + 1);
  }
  return String(indexZero + 1);
}
function getRatingValue(rawValue) {
  if (typeof rawValue !== "string" || rawValue === "") return null;
  const num = Number(rawValue);
  return Number.isFinite(num) && num > 0 ? num : null;
}
// Determine layout class based on question type and option count
function getLayoutClass(questionType, optionsCount, choiceDisplay) {
  // Only apply to single/multi/emotion questions rendered as radio/checkbox
  // groups — not the dedicated dropdown type, nor single/multi configured to
  // render as a dropdown (choice_display === "dropdown").
  if (questionType === 'dropdown') return '';
  // Likert renders its own matrix table — never the radio/checkbox layouts.
  if (questionType === 'likert') return '';
  if ((questionType === 'single' || questionType === 'multi') && choiceDisplay === 'dropdown') return '';

  // Horizontal for 2-3 options, grid for 4+
  if (optionsCount >= 4) return 'question-layout-grid';
  if (optionsCount >= 2) return 'question-layout-horizontal';
 
  return ''; // Fallback to vertical for 1 option
}
// A survey with real author-created sections carries a tab_label on each one
// (see survey_authoring.py's group_questions_into_sections); a flat/legacy
// survey has a single implicit "Survey" section with tab_label = null.
function isWizardSurvey(survey) {
  return (survey.sections || []).some((s) => s.tab_label);
}

function SectionHeading({ sec, index, wizard }) {
   if (!wizard) {
     return String(sec.title ?? "").trim() ? /* @__PURE__ */ jsx("h2", { children: sec.title }) : null;
   }
   return /* @__PURE__ */ jsxs("div", { className: "survey-section-heading", children: [
     /* @__PURE__ */ jsx("span", { className: "survey-section-badge", "aria-hidden": "true", children: index + 1 }),
     /* @__PURE__ */ jsx("h2", { children: sec.title || sec.tab_label })
   ] });
 }

function SectionBlock({ sec, index, wizard, answers, readOnly, setVal, setAnswers, toggleMulti, visible, errorKeys, numberByKey }) {
  // Branching: only questions on the answered path are rendered (progressive
  // reveal, like MS Forms). Hidden questions unmount, so their native
  // `required` attributes can't block submit. Off-path answers stay in state
  // (restored if the respondent changes their mind) but are filtered out of
  // the submit payload. Read-only mode (the publish-modal preview) is a
  // static review where nothing can be answered, so it shows every question
  // (visible is null there).
  const questions = visible ? sec.questions.filter((q) => visible.has(q.question_key)) : sec.questions;
  return /* @__PURE__ */ jsxs("section", { className: "form-section", children: [
    /* @__PURE__ */ jsx(SectionHeading, { sec, index, wizard }),
    questions.map((q) => /* @__PURE__ */ jsx(
      QuestionBlock,
      {
        q,
        cfg: q.config,
        options: q.options,
        answers,
        readOnly,
        setVal,
        setAnswers,
        toggleMulti,
        hasError: errorKeys ? errorKeys.has(q.question_key) : false,
        number: numberByKey ? numberByKey.get(q.question_key) : undefined
      },
      q.question_key
    ))
  ] });
}

function SurveyFields({
  survey,
  answers,
  setAnswers,
  readOnly,
  onSubmit,
  submitting,
  submitLabel = "Submit",
  submitError,
  // Bumped by the parent when the server rejects a submit with its own
  // MISSING_REQUIRED check (a safety net for cases our client-side check
  // missed) — forces the same highlighting a failed client-side attempt gets.
  highlightMissingSignal
}) {
  const sections = useMemo(() => survey.sections || [], [survey.sections]);
  const wizard = isWizardSurvey(survey);
  const [activeIndex, setActiveIndex] = useState(0);
  const { showError } = useSurveyUiToast();
  // Once true, missing required questions stay highlighted (they clear as
  // soon as they're answered) instead of just greying out Next/Submit — a
  // disabled button with no explanation is harder to act on than a highlight.
  // It intentionally never resets on its own: tab switching is always open
  // (see the tab bar below), so a respondent can jump back to an earlier
  // section after a failed submit and the highlight needs to still be there.
  const [attempted, setAttempted] = useState(false);
  useEffect(() => {
    // Sections can shrink (e.g. builder preview refresh after an edit) —
    // don't strand the active tab past the end of the list.
    if (activeIndex > sections.length - 1) setActiveIndex(Math.max(0, sections.length - 1));
  }, [sections.length, activeIndex]);
  useEffect(() => {
    if (highlightMissingSignal) setAttempted(true);
  }, [highlightMissingSignal]);

  function setVal(key, v) {
    if (readOnly) return;
    setAnswers((a) => ({ ...a, [key]: v }));
  }
  function toggleMulti(key, optKey) {
    if (readOnly) return;
    setAnswers((a) => {
      const cur = multiSelectedKeys(a, key);
      const has = cur.includes(optKey);
      const next = has ? cur.filter((x) => x !== optKey) : [...cur, optKey];
      return { ...a, [key]: next };
    });
  }

  // Numbering is global and continuous across every section, regardless of
  // branching visibility — a question's number doesn't shift as answers change.
  const numberByKey = useMemo(() => {
    if (!survey.show_question_numbers) return null;
    const m = new Map();
    let n = 0;
    for (const sec of sections) for (const q of sec.questions) m.set(q.question_key, ++n);
    return m;
  }, [survey.show_question_numbers, sections]);

  const visible = readOnly ? null : computeVisiblePath(survey, answers);
  // Section-level display logic: a section named as a "sec:<n>" target is
  // hidden (no tab, no questions) until an answer reveals it. Read-only
  // previews show everything, mirroring `visible`.
  const visibleSections = readOnly ? null : computeVisibleSections(survey, answers);
  const sectionShown = (i) => !visibleSections || visibleSections.has(i);
  const nextShownIndex = (from) => {
    for (let i = from + 1; i < sections.length; i++) if (sectionShown(i)) return i;
    return -1;
  };
  const prevShownIndex = (from) => {
    for (let i = from - 1; i >= 0; i--) if (sectionShown(i)) return i;
    return -1;
  };
  // If the active section got hidden (an earlier answer changed), fall back
  // to the nearest visible one so the respondent never sits on a ghost tab.
  useEffect(() => {
    if (!wizard || readOnly || sectionShown(activeIndex)) return;
    const fallback = prevShownIndex(activeIndex);
    setActiveIndex(fallback >= 0 ? fallback : Math.max(0, nextShownIndex(activeIndex)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard, readOnly, activeIndex, visibleSections]);
  const activeSection = wizard ? sections[activeIndex] : null;
  const nextIndex = wizard ? nextShownIndex(activeIndex) : -1;
  const isLastSection = !wizard || nextIndex === -1;
  const allMissing = sections.flatMap((sec) => missingRequiredKeys(sec, answers, visible));
  const sectionsWithMissing = wizard
    ? sections.filter((sec) => missingRequiredKeys(sec, answers, visible).length > 0)
    : [];
  const errorKeys = !readOnly && attempted ? new Set(allMissing) : null;

  function handleFormSubmit(e) {
    e.preventDefault();
    if (readOnly) return;
    if (wizard && !isLastSection) {
      const currentMissing = missingRequiredKeys(activeSection, answers, visible);
      if (currentMissing.length) {
        setAttempted(true);
        showError("Please fill all required questions.");
        return;
      }
      setActiveIndex(nextIndex);
      return;
    }
    if (allMissing.length) {
      setAttempted(true);
      showError("Please fill all required questions.");
      return;
    }
    onSubmit?.(e);
  }

  return /* @__PURE__ */ jsxs("form", { className: "survey-fields-form", onSubmit: handleFormSubmit, children: [
    wizard && /* @__PURE__ */ jsx("div", { className: "survey-tabbar", role: "tablist", "aria-label": "Survey sections", children: sections.map((sec, i) => ({ sec, i })).filter(({ i }) => sectionShown(i)).map(({ sec, i }) => {
      const hasMissing = attempted && sectionsWithMissing.includes(sec);
      return /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          role: "tab",
          "aria-selected": i === activeIndex,
          className: `survey-tab${i === activeIndex ? " is-active" : ""}${i < activeIndex ? " is-done" : ""}${hasMissing ? " has-error" : ""}`,
          onClick: () => setActiveIndex(i),
           children: [
             `${sec.tab_label}`,
             hasMissing && /* @__PURE__ */ jsx("span", { className: "survey-tab-error-exclam", "aria-hidden": "true", children: "!" })
           ]
        },
        i
      );
    }) }),
    wizard ? /* @__PURE__ */ jsx(
      SectionBlock,
      { sec: activeSection, index: activeIndex, wizard, answers, readOnly, setVal, setAnswers, toggleMulti, visible, errorKeys, numberByKey }
    ) : sections.map((sec, i) => sectionShown(i) && /* @__PURE__ */ jsx(
      SectionBlock,
      { sec, index: i, wizard, answers, readOnly, setVal, setAnswers, toggleMulti, visible, errorKeys, numberByKey },
      sec.title + sec.sort_order
    )),

    !readOnly && (wizard || onSubmit) && /* @__PURE__ */ jsxs("div", { className: "survey-wizard-actions", children: [
      wizard && prevShownIndex(activeIndex) >= 0 && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "btn secondary",
          onClick: () => setActiveIndex((i) => {
            const prev = prevShownIndex(i);
            return prev >= 0 ? prev : i;
          }),
          children: "← Back"
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "survey-wizard-actions-spacer" }),

      !isLastSection && /* @__PURE__ */ jsxs("button", { type: "submit", className: "btn primary", children: [
        "Next: ",
        sections[nextIndex]?.tab_label,
        " →"
      ] }),
      isLastSection && onSubmit && /* @__PURE__ */ jsx("button", { type: "submit", className: "btn primary", disabled: submitting, children: submitting ? "Submitting…" : submitLabel })
    ] })
  ] });
}
 
 
 
function QuestionBlock({
  q,
  cfg,
  options,
  answers,
  readOnly,
  setVal,
  setAnswers,
  toggleMulti,
  hasError,
  number
}) {
  const labelId = `${q.question_key}-label`;
  // choice_display is a shared config field for single and multi; either
  // getter reads the same value, so either works here regardless of q.type.
  const choiceDisplay = getMultiChoiceDisplay(cfg);
  const layoutClass = getLayoutClass(q.type, options.length, choiceDisplay);

  return /* @__PURE__ */ jsxs("div", { className: `field-block ${layoutClass}${hasError ? " has-error" : ""}`.trim(), children: [
    /* @__PURE__ */ jsxs("label", { className: "q-label", id: labelId, children: [
      number != null && /* @__PURE__ */ jsxs("span", { className: "q-number", children: [number, ". "] }),
      q.label,
      q.required && /* @__PURE__ */ jsx("span", { className: "req", children: "*" })
    ] }),
    hasError && /* @__PURE__ */ jsx("p", { className: "field-block-error", children: "This question is required." }),
    q.type === "text" && (() => {
      const variant = getTextVariant(cfg);
      const placeholder = getPlaceholder(cfg);
      const minLen = getMinLength(cfg);
      const maxLen = getMaxLength(cfg);
      return /* @__PURE__ */ jsx(
        "input",
        {
          type: variant,
          className: "q-input",
          "aria-labelledby": labelId,
          required: q.required,
          readOnly,
          placeholder,
          minLength: minLen,
          maxLength: maxLen,
          min: variant === "number" ? getNumber(cfg, "min") : void 0,
          max: variant === "number" ? getNumber(cfg, "max") : void 0,
          step: variant === "number" ? getNumber(cfg, "step") : void 0,
          value: answers[q.question_key] ?? "",
          onChange: (e) => setVal(q.question_key, e.target.value)
        }
      );
    })(),
    q.type === "textarea" && /* @__PURE__ */ jsx(
      "textarea",
      {
        required: q.required,
        readOnly,
        "aria-labelledby": labelId,
        rows: getTextareaRows(cfg),
        placeholder: getPlaceholder(cfg),
        maxLength: getMaxLength(cfg),
        value: answers[q.question_key] ?? "",
        onChange: (e) => setVal(q.question_key, e.target.value)
      }
    ),
    q.type === "rating" && (() => {
      const scale = getRatingScale(cfg);
      const style = getRatingStyle(cfg);
      const followUp = getRatingFollowUp(cfg);
      const fk = ratingFollowupAnswerKey(q.question_key);
      const ratingValue = getRatingValue(answers[q.question_key]);
      const ratingChosen = ratingValue != null;
      const isIconRating = style === "stars" || style === "hearts";
      const lowEnabled = getLowRatingEnabled(cfg);
      const lowThreshold = getLowRatingThreshold(cfg);
      const lowOptions = getLowRatingOptions(cfg);
      const lowAllowOther = getLowRatingAllowOther(cfg);
      const rk = lowRatingReasonsKey(q.question_key);
      const ok = lowRatingOtherKey(q.question_key);
      const showLow = lowEnabled && ratingChosen && ratingValue <= lowThreshold && lowOptions.length > 0;
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: `rating-row rating-row--${style}`, role: "radiogroup", "aria-labelledby": labelId, children: isIconRating ? /* @__PURE__ */ jsx(
          Rating,
          {
            name: q.question_key,
            max: scale,
            value: ratingValue,
            readOnly,
            onChange: (_event, nextValue) => {
              if (readOnly) return;
              const next = typeof nextValue === "number" ? String(nextValue) : "";
              setAnswers((a) => ({ ...a, [q.question_key]: next, [fk]: "", [rk]: [], [ok]: "" }));
            },
            icon: style === "stars" ? /* @__PURE__ */ jsx(StarIcon, { fontSize: "inherit" }) : /* @__PURE__ */ jsx(FavoriteIcon, { fontSize: "inherit" }),
            emptyIcon: style === "stars" ? /* @__PURE__ */ jsx(StarBorderIcon, { fontSize: "inherit" }) : /* @__PURE__ */ jsx(FavoriteBorderIcon, { fontSize: "inherit" }),
            className: `rating-control rating-control--${style}`,
            "aria-labelledby": labelId
          }
        ) : Array.from({ length: scale }, (_, i) => {
          const value = String(i + 1);
          const display = ratingDisplay(style, i, scale);
          return /* @__PURE__ */ jsxs("label", { className: `rating-option rating-option--${style}`, title: value, children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "radio",
                name: q.question_key,
                value,
                required: q.required,
                disabled: readOnly,
                checked: answers[q.question_key] === value,
                onChange: () => {
                  if (readOnly) return;
                  setAnswers((a) => ({ ...a, [q.question_key]: value, [fk]: "", [rk]: [], [ok]: "" }));
                }
              }
            ),
            /* @__PURE__ */ jsx("span", { children: display })
          ] }, value);
        }) }),
        followUp && ratingChosen && /* @__PURE__ */ jsxs("div", { className: "rating-follow-up", children: [
          /* @__PURE__ */ jsxs("label", { className: "rating-follow-up-label", htmlFor: fk, children: [
            "Can you briefly explain why you chose this rating?",
            " ",
            /* @__PURE__ */ jsx("span", { className: "rating-follow-up-optional", children: "(optional)" })
          ] }),
          /* @__PURE__ */ jsx(
            "textarea",
            {
              id: fk,
              className: "rating-follow-up-textarea",
              rows: 3,
              readOnly,
              maxLength: RATING_FOLLOWUP_MAX_LEN,
              placeholder: "Your feedback helps us improve\u2026",
              value: answers[fk] ?? "",
              onChange: (e) => setVal(fk, e.target.value)
            }
          )
        ] }),
        showLow && /* @__PURE__ */ jsxs("div", { className: "rating-low", children: [
          /* @__PURE__ */ jsxs("p", { className: "rating-low-title", children: [
            /* @__PURE__ */ jsx("span", { children: "What made this a low rating?" }),
            /* @__PURE__ */ jsx("span", { className: "rating-low-optional", children: "Select any that apply · optional" })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "rating-low-chips", role: "group", "aria-labelledby": labelId, children: lowOptions.map((o) => {
            const checked = lowRatingSelectedKeys(answers, rk).includes(o.key);
            return /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: `rating-low-chip${checked ? " is-selected" : ""}`,
                "aria-pressed": checked,
                disabled: readOnly,
                onClick: () => {
                  if (readOnly) return;
                  setAnswers((a) => {
                    const cur = lowRatingSelectedKeys(a, rk);
                    const has = cur.includes(o.key);
                    const next = has ? cur.filter((x) => x !== o.key) : [...cur, o.key];
                    return { ...a, [rk]: next };
                  });
                },
                children: o.label
              },
              o.key
            );
          }) }),
          lowAllowOther && /* @__PURE__ */ jsx(
            "textarea",
            {
              className: "rating-follow-up-textarea rating-low-other",
              rows: 2,
              readOnly,
              maxLength: LOW_RATING_OTHER_MAX_LEN,
              placeholder: "Other (please specify)…",
              value: answers[ok] ?? "",
              onChange: (e) => setVal(ok, e.target.value)
            }
          )
        ] })
      ] });
    })(),
 
    (q.type === "single" || q.type === "emotion") && (() => {    
      const singleDisplay = q.type === "single" ? getSingleChoiceDisplay(cfg) : "standard";
 
      return singleDisplay === "dropdown" ? (
        <SingleSelectDropdown
          questionKey={q.question_key}
          options={options}
          hint={getPlaceholder(cfg)}
          readOnly={readOnly}
          selected={singleSelectedKey(answers, q.question_key)}
          required={q.required}
          textValues={choiceTextValues(answers, q.question_key)}
          onChangeSelected={(next) => setVal(q.question_key, next)}
          labelId={labelId}
        />
      ) : (
        <ChoiceGroup
          q={q}
          options={options}
          inputType="radio"
          answers={answers}
          readOnly={readOnly}
          setVal={setVal}
          toggleMulti={toggleMulti}
          emotion={q.type === "emotion"}
          labelId={labelId}
        />
      );
    })(),
 
    q.type === "multi" && /* @__PURE__ */ jsx(Fragment, { children: getMultiChoiceDisplay(cfg) === "dropdown" ? /* @__PURE__ */ jsx(
      MultiSelectDropdown,
      {
        questionKey: q.question_key,
        options,
        hint: getPlaceholder(cfg),
        readOnly,
        selected: multiSelectedKeys(answers, q.question_key),
        textValues: choiceTextValues(answers, q.question_key),
        onChangeSelected: (next) => setVal(q.question_key, next),
        labelId
      }
    ) : /* @__PURE__ */ jsx(
      ChoiceGroup,
      {
        q,
        options,
        inputType: "checkbox",
        answers,
        readOnly,
        setVal,
        toggleMulti,
        emotion: false,
        labelId
      }
    ) }),
    q.type === "dropdown" && /* @__PURE__ */ jsx(
      SingleSelectDropdown,
      {
        questionKey: q.question_key,
        options,
        hint: getPlaceholder(cfg),
        readOnly,
        selected: typeof answers[q.question_key] === "string" ? answers[q.question_key] : "",
        required: q.required,
        onChangeSelected: (next) => setVal(q.question_key, next),
        labelId
      }
    ),
    q.type === "likert" && /* @__PURE__ */ jsx(
      LikertMatrix,
      { q, cfg, options, answers, readOnly, setVal, labelId }
    )
  ] });
}
function likertRows(answers, key) {
  const v = answers[key];
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}
function LikertMatrix({ q, cfg, options, answers, readOnly, setVal, labelId }) {
  const points = getLikertScalePoints(cfg);
  const rows = likertRows(answers, q.question_key);
  // Forced ranking: each scale value belongs to at most one row. The column
  // behaves like a single-select — picking a value already used on another
  // row moves it here and clears it there (server still enforces uniqueness).
  const uniqueColumns = getLikertUniqueColumns(cfg);
  function setRow(rowKey, pointKey) {
    if (readOnly) return;
    const cur = likertRows(answers, q.question_key);
    const next = { ...cur, [rowKey]: pointKey };
    if (uniqueColumns) {
      for (const [rk, pk] of Object.entries(cur)) {
        if (rk !== rowKey && pk === pointKey) delete next[rk];
      }
    }
    setVal(q.question_key, next);
  }
  return /* @__PURE__ */ jsx("div", { className: "likert-matrix-scroll", children: /* @__PURE__ */ jsxs("table", { className: "likert-matrix", role: "group", "aria-labelledby": labelId, children: [
    /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
      /* @__PURE__ */ jsx("th", { className: "likert-corner", scope: "col" }),
      points.map((p) => /* @__PURE__ */ jsx("th", { scope: "col", className: "likert-point-head", children: p.label }, p.key))
    ] }) }),
    /* @__PURE__ */ jsx("tbody", { children: options.map((o) => /* @__PURE__ */ jsxs("tr", { className: "likert-row", children: [
      /* @__PURE__ */ jsxs("th", { scope: "row", className: "likert-row-label", children: [
        o.label,
        o.description && /* @__PURE__ */ jsx("span", { className: "option-description", children: optionDescription(o) })
      ] }),
      points.map((p) => /* @__PURE__ */ jsx("td", { className: "likert-cell", children: /* @__PURE__ */ jsx(
        "input",
        {
          type: "radio",
          name: `${q.question_key}__${o.option_key}`,
          value: p.key,
          "aria-label": `${o.label}: ${p.label}`,
          disabled: readOnly,
          checked: rows[o.option_key] === p.key,
          onChange: () => setRow(o.option_key, p.key)
        }
      ) }, p.key))
    ] }, o.option_key)) })
  ] }) });
}
 
 
function MultiSelectDropdown({
  questionKey,
  options,
  hint,
  readOnly,
  selected,
  textValues,
  onChangeSelected,
  labelId
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    searchRef.current?.focus();
  }, [open]);
  function toggleKey(k) {
    if (readOnly) return;
    const has = selected.includes(k);
    const nextValues = has ? selected.filter((x) => x !== k) : [...selected, k];
    const nextTextValues = { ...textValues };
    if (has) delete nextTextValues[k];
    onChangeSelected({ values: nextValues, textValues: nextTextValues });
  }
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = normalizedQuery ? options.filter((o) => `${o.label ?? ""} ${o.description ?? ""}`.toLowerCase().includes(normalizedQuery)) : options;
  const summary = selected.length === 0 ? "Select one or more\u2026" : selected.map((k) => {
    const option = options.find((o) => o.option_key === k);
    if (isOtherOption(option)) {
      const typed = typeof textValues[k] === "string" ? textValues[k].trim() : "";
      return typed || option.label || k;
    }
    return option?.label ?? k;
  }).join(", ");
  return /* @__PURE__ */ jsxs("div", { className: "multi-dropdown", ref: rootRef, children: [
    hint ? /* @__PURE__ */ jsx("p", { className: "muted small multi-dropdown-hint", children: hint }) : null,
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        id: `${questionKey}-multi-trigger`,
        className: "multi-dropdown-trigger",
        "aria-haspopup": "listbox",
        "aria-expanded": open,
        "aria-labelledby": labelId,
        disabled: readOnly,
        onClick: () => !readOnly && setOpen((o) => !o),
        children: [
          /* @__PURE__ */ jsx("span", { className: selected.length === 0 ? "multi-dropdown-placeholder" : void 0, children: summary }),
          /* @__PURE__ */ jsx("span", { className: "multi-dropdown-chevron", "aria-hidden": true, children: "\u25BE" })
        ]
      }
    ),
    open && /* @__PURE__ */ jsxs("div", { className: "multi-dropdown-panel", role: "listbox", "aria-labelledby": `${questionKey}-multi-trigger`, children: [
      /* @__PURE__ */ jsx("div", { className: "dropdown-search-wrap", children: /* @__PURE__ */ jsx("input", { ref: searchRef, type: "text", className: "dropdown-search-input", placeholder: "Type to filter options...", value: query, onChange: (e) => setQuery(e.target.value), onKeyDown: (e) => e.stopPropagation() }) }),
      filteredOptions.length === 0 ? /* @__PURE__ */ jsx("div", { className: "dropdown-search-empty", children: "No matching options" }) : filteredOptions.map((o) => /* @__PURE__ */ jsxs("div", { className: isOtherOption(o) ? "dropdown-other-wrap" : void 0, children: [/* @__PURE__ */ jsxs("label", { className: "multi-dropdown-row", children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "checkbox",
            checked: selected.includes(o.option_key),
            disabled: readOnly,
            onChange: () => toggleKey(o.option_key)
          }
        ),
        /* @__PURE__ */ jsxs("span", { children: [
          o.label,
          o.description && /* @__PURE__ */ jsx("span", { className: "option-description", children: optionDescription(o) })
        ] })
      ] }), isOtherOption(o) && selected.includes(o.option_key) && /* @__PURE__ */ jsx("input", { className: "dropdown-other-input", type: "text", value: textValues[o.option_key] ?? "", readOnly, maxLength: 500, placeholder: OTHER_TEXT_PLACEHOLDER, "aria-label": `${o.label}: ${OTHER_TEXT_PLACEHOLDER}`, onChange: (e) => onChangeSelected({ values: selected, textValues: { ...textValues, [o.option_key]: e.target.value } }) })] }, o.option_key))
    ] })
  ] });
}
function SingleSelectDropdown({
  questionKey,
  options,
  hint,
  readOnly,
  selected,
  required,
  textValues,
  onChangeSelected,
  labelId
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    searchRef.current?.focus();
  }, [open]);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = normalizedQuery ? options.filter((o) => `${o.label ?? ""} ${o.description ?? ""}`.toLowerCase().includes(normalizedQuery)) : options;
  const selectedLabel = selected ? (() => {
    const option = options.find((o) => o.option_key === selected);
    if (isOtherOption(option)) {
      const typed = typeof textValues[selected] === "string" ? textValues[selected].trim() : "";
      return typed || option.label || selected;
    }
    return option?.label ?? selected;
  })() : "";
  const placeholder = hint ?? "Select\u2026";
  return /* @__PURE__ */ jsxs("div", { className: "single-dropdown", ref: rootRef, children: [
    hint ? /* @__PURE__ */ jsx("p", { className: "muted small single-dropdown-hint", children: hint }) : null,
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        id: `${questionKey}-single-trigger`,
        className: "single-dropdown-trigger",
        "aria-haspopup": "listbox",
        "aria-expanded": open,
        "aria-labelledby": labelId,
        "aria-required": required || void 0,
        disabled: readOnly,
        onClick: () => !readOnly && setOpen((o) => !o),
        children: [
          /* @__PURE__ */ jsx("span", { className: selected ? void 0 : "single-dropdown-placeholder", children: selected ? selectedLabel : placeholder }),
          /* @__PURE__ */ jsx("span", { className: "single-dropdown-chevron", "aria-hidden": true, children: "\u25BE" })
        ]
      }
    ),
    open && /* @__PURE__ */ jsxs("div", { className: "single-dropdown-panel", role: "listbox", "aria-labelledby": `${questionKey}-single-trigger`, children: [
      /* @__PURE__ */ jsx("div", { className: "dropdown-search-wrap", children: /* @__PURE__ */ jsx("input", { ref: searchRef, type: "text", className: "dropdown-search-input", placeholder: "Type to filter options...", value: query, onChange: (e) => setQuery(e.target.value), onKeyDown: (e) => e.stopPropagation() }) }),
      filteredOptions.length === 0 ? /* @__PURE__ */ jsx("div", { className: "dropdown-search-empty", children: "No matching options" }) : filteredOptions.map((o) => {
        const isSelected = selected === o.option_key;
        return /* @__PURE__ */ jsxs("div", { className: isOtherOption(o) ? "dropdown-other-wrap" : void 0, children: [/* @__PURE__ */ jsxs("button", { type: "button", className: `single-dropdown-row${isSelected ? " is-selected" : ""}`, role: "option", "aria-selected": isSelected, disabled: readOnly, onClick: () => {
          if (readOnly) return;
          const nextTextValues = isOtherOption(o) ? textValues : {};
          onChangeSelected({ value: o.option_key, textValues: nextTextValues });
          setOpen(false);
        }, children: [
          /* @__PURE__ */ jsxs("span", { className: "single-dropdown-label", children: [
            o.label,
            o.description && /* @__PURE__ */ jsx("span", { className: "option-description", children: optionDescription(o) })
          ] }),
          isSelected && /* @__PURE__ */ jsx("span", { className: "single-dropdown-check", "aria-hidden": true, children: "\u2713" })
        ] }, o.option_key), isOtherOption(o) && isSelected && /* @__PURE__ */ jsx("input", { className: "dropdown-other-input", type: "text", value: textValues[o.option_key] ?? "", readOnly, maxLength: 500, placeholder: OTHER_TEXT_PLACEHOLDER, "aria-label": `${o.label}: ${OTHER_TEXT_PLACEHOLDER}`, onChange: (e) => onChangeSelected({ value: o.option_key, textValues: { ...textValues, [o.option_key]: e.target.value } }), onClick: (e) => e.stopPropagation() })] }, o.option_key);
      })
    ] })
  ] });
}
function ChoiceGroup({
  q,
  options,
  inputType,
  answers,
  readOnly,
  setVal,
  toggleMulti,
  emotion,
  labelId
}) {
  const multiKeys = inputType === "checkbox" ? multiSelectedKeys(answers, q.question_key) : [];
  const singleValue = singleSelectedKey(answers, q.question_key);
  const textValues = choiceTextValues(answers, q.question_key);
  return /* @__PURE__ */ jsx("div", { className: "options", role: "group", "aria-labelledby": labelId, children: options.map((o, oi) => /* @__PURE__ */ jsxs("div", { className: isOtherOption(o) ? "choice-other-wrap" : void 0, children: [/* @__PURE__ */ jsxs("label", { className: "option-row", children: [
    inputType === "radio" ? /* @__PURE__ */ jsx(
      "input",
      {
        type: "radio",
        name: q.question_key,
        value: o.option_key,
        required: q.required && oi === 0,
        disabled: readOnly,
        checked: singleValue === o.option_key,
        onChange: () => setVal(q.question_key, { value: o.option_key, textValues: isOtherOption(o) ? textValues : {} })
      }
    ) : /* @__PURE__ */ jsx(
      "input",
      {
        type: "checkbox",
        disabled: readOnly,
        checked: multiKeys.includes(o.option_key),
        onChange: () => toggleMulti(q.question_key, o.option_key)
      }
    ),
    (q.type === "single" || q.type === "multi") && o.description ? /* @__PURE__ */ jsxs("span", { className: "option-label-block", children: [
      /* @__PURE__ */ jsx("span", { className: "option-label-title", children: o.label }),
      /* @__PURE__ */ jsx("span", { className: "option-description", children: optionDescription(o) })
    ] }) : emotion ? /* @__PURE__ */ jsx("span", { className: "emotion-label", children: o.label }) : o.label
  ] }), isOtherOption(o) && ((inputType === "checkbox" && multiKeys.includes(o.option_key)) || (inputType === "radio" && singleValue === o.option_key)) && /* @__PURE__ */ jsx("input", { className: "choice-other-input", type: "text", value: textValues[o.option_key] ?? "", readOnly, maxLength: 500, placeholder: OTHER_TEXT_PLACEHOLDER, "aria-label": `${o.label}: ${OTHER_TEXT_PLACEHOLDER}`, onChange: (e) => {
    const nextTextValues = { ...textValues, [o.option_key]: e.target.value };
    if (inputType === "checkbox") {
      setVal(q.question_key, { values: multiKeys, textValues: nextTextValues });
    } else {
      setVal(q.question_key, { value: o.option_key, textValues: nextTextValues });
    }
  } })] }, o.option_key)) });
}
function buildSubmitPayload(survey, answers) {
  // Display logic: answers to conditionally hidden questions are omitted from
  // the wire payload — they aren't shown for this combination of answers. The
  // server drops hidden-question answers too; this keeps the payload honest.
  const visible = computeVisiblePath(survey, answers);
  const flat = survey.sections.flatMap((s) => s.questions).filter((q) => visible.has(q.question_key));
  return flat.map((q) => {
    const v = answers[q.question_key];
    // Choice answers: the "Other" option's typed text travels inside the
    // selected keys as "other:<text>" (see submittedOptionKey); value_text is
    // not used for choice questions.
    if (q.type === "multi") {
      const keys = Array.isArray(v) ? v : v && typeof v === "object" && Array.isArray(v.values) ? v.values : [];
      const textValues = v && typeof v === "object" && !Array.isArray(v) && v.textValues && typeof v.textValues === "object" ? v.textValues : {};
      return { question_key: q.question_key, selected_option_keys: keys.map((key) => submittedOptionKey(q, key, textValues)), value_text: null };
    }
    if (q.type === "single" || q.type === "dropdown" || q.type === "emotion") {
      const s = typeof v === "string" ? v : v && typeof v === "object" && !Array.isArray(v) && typeof v.value === "string" ? v.value : "";
      const textValues = v && typeof v === "object" && !Array.isArray(v) && v.textValues && typeof v.textValues === "object" ? v.textValues : {};
      return { question_key: q.question_key, selected_option_keys: s ? [submittedOptionKey(q, s, textValues)] : [], value_text: null };
    }
    if (q.type === "likert") {
      // Matrix serializes into one answer: {"rows": {row_option_key: point_key}}.
      const rowsObj = v && typeof v === "object" && !Array.isArray(v) ? v : {};
      const clean = {};
      for (const o of q.options || []) {
        const pk = rowsObj[o.option_key];
        if (typeof pk === "string" && pk) clean[o.option_key] = pk;
      }
      const hasRows = Object.keys(clean).length > 0;
      return { question_key: q.question_key, value_text: hasRows ? JSON.stringify({ rows: clean }) : "" };
    }
    if (q.type === "rating" && (getRatingFollowUp(q.config) || getLowRatingEnabled(q.config))) {
      const score = typeof v === "string" ? v : "";
      if (!score) {
        return { question_key: q.question_key, value_text: "" };
      }
      const payload = { v: score };
      if (getRatingFollowUp(q.config)) {
        const reasonRaw = answers[ratingFollowupAnswerKey(q.question_key)];
        const reason = typeof reasonRaw === "string" ? reasonRaw.trim() : "";
        if (reason) payload.r = reason;
      }
      if (getLowRatingEnabled(q.config)) {
        const scoreNum = Number(score);
        if (Number.isFinite(scoreNum) && scoreNum <= getLowRatingThreshold(q.config)) {
          const opts = getLowRatingOptions(q.config);
          const selectedKeys = lowRatingSelectedKeys(answers, lowRatingReasonsKey(q.question_key));
          const labels = selectedKeys
            .map((k) => opts.find((o) => o.key === k)?.label)
            .filter(Boolean);
          if (labels.length) payload.s = labels;
          if (getLowRatingAllowOther(q.config)) {
            const otherRaw = answers[lowRatingOtherKey(q.question_key)];
            const other = typeof otherRaw === "string" ? otherRaw.trim() : "";
            if (other) payload.o = other;
          }
        }
      }
      return { question_key: q.question_key, value_text: JSON.stringify(payload) };
    }
    return { question_key: q.question_key, value_text: typeof v === "string" ? v : "" };
  });
}
export {
  SurveyFields,
  buildSubmitPayload,
  ratingFollowupAnswerKey
};
 
 